import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntegrationData, SendToIntegrationContainerData } from '../types';
import { SendToIntegrationModal } from './SendToIntegrationModal';
import { Button, Icon, TooltipTabstop } from '@deque/cauldron-react';
import { type SendIssueEventArgs } from '../hooks/useSendToIntegrationLogic';

interface SendToIntegrationContainerProps {
  apiHostname: string;
  authFn: () => Promise<string>;
  userEnterpriseId: string;
  selectedIntegrationData: IntegrationData | null;
  sendToIntegrationContainerData: SendToIntegrationContainerData;
  onModalClose: () => void;
  onSendIssueSuccess: (args: SendIssueEventArgs) => void;
  setsSelectedIntegrationData: (integrationData: IntegrationData) => void;
}

export const SendToIntegrationContainer = ({
  apiHostname,
  authFn,
  userEnterpriseId,
  selectedIntegrationData,
  sendToIntegrationContainerData,
  onModalClose,
  onSendIssueSuccess,
  setsSelectedIntegrationData
}: SendToIntegrationContainerProps) => {
  const { t } = useTranslation();
  const { integrationData, issues } = sendToIntegrationContainerData;

  // temporary solution to check if the issue is already sent to some integration to save previous implementation
  // TODO: remove this once when we allow to send the same issue to multiple integrations
  const isIssueAlreadySentToSomeIntegration = useMemo(
    () => !!issues[0]?.integrationUrl,
    [issues]
  );

  return (
    <>
      {selectedIntegrationData && (
        <SendToIntegrationModal
          selectedIntegrationData={selectedIntegrationData}
          sendToIntegrationContainerData={sendToIntegrationContainerData}
          apiHostname={apiHostname}
          authFn={authFn}
          userEnterpriseId={userEnterpriseId}
          closeFn={onModalClose}
          onSuccess={onSendIssueSuccess}
        />
      )}
      {integrationData.map(integrationConfig => (
        <Fragment key={integrationConfig.productSlug}>
          {integrationConfig.canViewIssue &&
            integrationConfig.sentIssueLinks.map(sentIssueLink => (
              <Button
                key={sentIssueLink}
                thin
                variant="secondary"
                onClick={() => {
                  window.open(sentIssueLink, '_blank', 'noopener,noreferrer');
                }}
              >
                {t(`OPEN IN {{ integration }}`, {
                  integration: integrationConfig.productName.toLocaleUpperCase()
                })}
              </Button>
            ))}

          {integrationConfig.alreadySentForMultiple && (
            <TooltipTabstop
              tooltip={t(
                `1 or more of the issues below have already been shared to {{ integration }}`,
                {
                  integration: integrationConfig.productName
                }
              )}
            >
              <Button thin variant="secondary" disabled>
                <Icon type="share-nodes" />
                {t('SHARE {{ multipleText }}TO {{ integration }}', {
                  multipleText: integrationConfig.isMultipleIssues
                    ? t('ALL ')
                    : '',
                  integration: integrationConfig.productName.toLocaleUpperCase()
                })}
              </Button>
            </TooltipTabstop>
          )}
          {integrationConfig.canSendIssue &&
            !isIssueAlreadySentToSomeIntegration && (
              <Button
                thin
                variant="secondary"
                onClick={() => setsSelectedIntegrationData(integrationConfig)}
              >
                <Icon type="share-nodes" />
                {integrationConfig.isMultipleIssues
                  ? t('SHARE ALL TO {{ integration }}', {
                      integration:
                        integrationConfig.productName.toLocaleUpperCase()
                    })
                  : t('SHARE TO {{ integration }}', {
                      integration:
                        integrationConfig.productName.toLocaleUpperCase()
                    })}
              </Button>
            )}
        </Fragment>
      ))}
    </>
  );
};
